/**
 * Shared types for the onboarding flow.
 *
 * The profile is captured locally; in pilot it's persisted to localStorage
 * so the rest of the app can personalise (welcome banner, default fleet
 * filter on the map, default questionnaire on new inspection). When the
 * back-end gets a `user_profiles.preferences` JSON column, the same shape
 * lifts up to the server with no caller changes.
 */

export const STEPS = [
  'welcome',
  'profile',
  'data-depth',
  'capabilities',
  'sample-inspection',
  'completion',
] as const;
export type StepId = typeof STEPS[number];

export const STEP_LABELS: Record<StepId, string> = {
  welcome:             'Welcome',
  profile:             'Your work',
  'data-depth':        'Behind the scenes',
  capabilities:        'What you can do',
  'sample-inspection': 'Try it',
  completion:          'Set sail',
};

export type Role =
  | 'inspector_lead'
  | 'inspector_assist'
  | 'vetting_manager'
  | 'tech_super'
  | 'auditor'
  | 'other';

export const ROLES: { id: Role; label: string; sub: string }[] = [
  { id: 'inspector_lead',   label: 'Lead inspector',          sub: 'I run inspections and sign reports.' },
  { id: 'inspector_assist', label: 'Assistant inspector',     sub: 'I support a lead during inspections.' },
  { id: 'vetting_manager',  label: 'Vetting manager',         sub: 'I review and approve inspection outcomes.' },
  { id: 'tech_super',       label: 'Technical superintendent', sub: 'I manage compliance for a fleet.' },
  { id: 'auditor',          label: 'Internal auditor / SMS',  sub: 'I run audit cycles, not vetting.' },
  { id: 'other',            label: 'Something else',          sub: 'Tell us in onboarding settings later.' },
];

export const REGIMES = [
  'CDI 10.5',
  'SIRE 2.0',
  'TMSA 3',
  'RightShip',
  'OCIMF MTIS',
  'ISM',
  'ISPS',
  'MLC 2006',
] as const;
export type Regime = typeof REGIMES[number];

export const FLEETS = [
  { id: 'tanker_chem',  label: 'Chemical tankers' },
  { id: 'tanker_oil',   label: 'Oil tankers' },
  { id: 'gas',          label: 'Gas / LNG / LPG' },
  { id: 'bulk',         label: 'Bulk carriers' },
  { id: 'container',    label: 'Container ships' },
  { id: 'offshore',     label: 'Offshore / OSV' },
  { id: 'general',      label: 'General cargo' },
  { id: 'passenger',    label: 'Passenger / RoRo' },
] as const;
export type FleetId = typeof FLEETS[number]['id'];

export interface OnboardingProfile {
  role: Role | null;
  regimes: Regime[];
  fleets: FleetId[];
  yearsExperience: number;
}

export const DEFAULT_PROFILE: OnboardingProfile = {
  role: null,
  regimes: [],
  fleets: [],
  yearsExperience: 5,
};

/** localStorage keys — namespaced so they don't collide with other modules. */
export const LS_KEY_COMPLETED = 'varuna.onboarding.completedAt';
export const LS_KEY_PROFILE   = 'varuna.onboarding.profile';
